import React from 'react';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { AnalysisStep } from '../context/AppContext';

interface ProgressStepperProps {
  currentStep: AnalysisStep;
  error?: string | null;
}

const STEPS: { key: AnalysisStep; label: string }[] = [
  { key: 'fetching', label: 'Fetching playlist' },
  { key: 'analyzing', label: 'Analyzing tracks' },
  { key: 'building', label: 'Building heatmap' },
];

const STEP_ORDER: AnalysisStep[] = ['fetching', 'analyzing', 'building'];

const ProgressStepper: React.FC<ProgressStepperProps> = ({ currentStep, error }) => {
  const currentIndex = STEP_ORDER.indexOf(currentStep);
  const isError = currentStep === 'error';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // When in error state, find the last active step (the one that was running when error occurred)
  // We use the error state to show which step failed — default to last step if not found
  const errorStepIndex = isError ? Math.max(0, STEP_ORDER.length - 1) : -1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'center',
        gap: { xs: 1.5, sm: 3 },
        py: 2,
      }}
    >
      {STEPS.map((step, index) => {
        const isCompleted = !isError && currentIndex > index;
        const isActive = !isError && currentIndex === index;
        const isErrorStep = isError && index === errorStepIndex;
        const isPending = !isCompleted && !isActive && !isErrorStep;

        return (
          <Box
            key={step.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {/* Step icon */}
            {isCompleted && (
              <CheckCircleIcon sx={{ fontSize: 20, color: '#4caf50' }} />
            )}
            {isActive && (
              <CircularProgress size={20} thickness={4} sx={{ color: '#3B82F6' }} />
            )}
            {isErrorStep && (
              <ErrorIcon sx={{ fontSize: 20, color: '#EF4444' }} />
            )}
            {isPending && (
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: `2px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                  flexShrink: 0,
                }}
              />
            )}

            {/* Step label */}
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: isActive ? 700 : 500,
                color: isCompleted
                  ? '#4caf50'
                  : isActive
                    ? theme.palette.text.primary
                    : isErrorStep
                      ? '#EF4444'
                      : isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
              }}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProgressStepper;
